import { useEffect, useRef } from "react";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { isNativeApp } from "./capacitor";
import { toNativePath } from "./native-routes";
import { useAuth } from "./auth-provider";
import { getLastUserId, clearSessionData, setLastUserId } from "./user-storage";

export function AppResumeHandler() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const lastCheckRef = useRef<number>(0);
  
  useEffect(() => {
    if (!isNativeApp()) {
      return;
    }
    
    const handleAppStateChange = async (state: { isActive: boolean }) => {
      if (!state.isActive) {
        return;
      }
      
      const now = Date.now();
      if (now - lastCheckRef.current < 1000) {
        return;
      }
      lastCheckRef.current = now;
      
      console.log("[AppResumeHandler] App resumed, checking user state");
      
      if (isLoading) {
        console.log("[AppResumeHandler] Auth still loading, skipping check");
        return;
      }
      
      const lastUserId = getLastUserId();
      const currentUserId = user?.id || null;
      
      if (isAuthenticated && currentUserId && lastUserId && currentUserId !== lastUserId) {
        console.log("[AppResumeHandler] CRITICAL: User mismatch detected!", {
          lastUserId,
          currentUserId,
        });
        
        // Clear session data only - preferences are preserved per-user
        clearSessionData(lastUserId);
        setLastUserId(currentUserId);
        
        window.location.reload();
        return;
      }
      
      if (isAuthenticated && currentUserId && !lastUserId) {
        console.log("[AppResumeHandler] First login detected, setting user ID");
        setLastUserId(currentUserId);
      }
      
      if (!isAuthenticated && lastUserId) {
        console.log("[AppResumeHandler] User logged out, clearing session data");
        clearSessionData(lastUserId);
      }
    };
    
    let handle: { remove: () => void } | null = null;
    
    App.addListener("appStateChange", handleAppStateChange)
      .then((h) => {
        handle = h;
      })
      .catch((e) => {
        console.warn("[AppResumeHandler] Failed to add listener:", e);
      });
    
    return () => {
      if (handle) {
        handle.remove();
      }
    };
  }, [user?.id, isAuthenticated, isLoading]);

  // Deep links. Nothing was listening for appUrlOpen, so when iOS handed back
  // shiftcanvas:// (after logout, or from a notification/Universal Link) the
  // app came to the foreground and then just sat there. Capacitor also has to
  // close the in-app browser itself — the web page cannot close it.
  useEffect(() => {
    if (!isNativeApp()) return;

    let handle: { remove: () => void } | null = null;

    const handleUrlOpen = async (event: { url: string }) => {
      console.log("[AppResumeHandler] Deep link:", event.url);
      try {
        await Browser.close();
      } catch {
        // No browser open — fine.
      }

      // shiftcanvas://auth-complete — the auth/logout round trip finished.
      // Reload so the app re-reads session state rather than trusting stale
      // in-memory auth.
      if (/auth-complete/i.test(event.url)) {
        window.location.href = "/app";
        return;
      }

      // shiftcanvas://app/<path> or https://shiftcanvas.com.au/<path>
      const path = event.url
        .replace(/^shiftcanvas:\/\//i, "/")
        .replace(/^https?:\/\/[^/]+/i, "");
      const target = toNativePath(path);
      if (target) {
        window.location.href = target;
      }
    };

    App.addListener("appUrlOpen", handleUrlOpen)
      .then((h) => {
        handle = h;
      })
      .catch((e) => {
        console.warn("[AppResumeHandler] Failed to add appUrlOpen listener:", e);
      });

    return () => {
      if (handle) handle.remove();
    };
  }, []);

  return null;
}
